import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import axios from 'axios';
import { getCameraDataWithRefreshedGains } from 'src/services/getCameraDataWithRefreshedGains';
import useIdbSync from 'src/services/idbSync/useIdbSync';
import { updateExamFieldsIDB } from 'src/services/offline/outboxClient';
import {
  CameraConnectionIssueReason,
  captureCameraEvent,
  EXAM_EVENTS,
  NETWORK_TRANSPORT,
  withNetworkTiming,
} from 'src/services/posthogEvents';
import { reportCameraConnectionIssue } from 'src/services/reportCameraConnectionIssue';
import Sentry from 'src/services/sentry';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CameraConnection from 'src/models/Camera/CameraConnection';
import {
  CaptureModeEnum,
  StatusOfShootingEnum,
} from 'src/models/Camera/Shooting/Shooting.model';
import { CameraData } from 'src/models/CameraData.model';
import { EyePropertyCode } from 'src/models/Eye.model';
import { I18NextNS } from 'src/models/Language.model';
import { EyesPosition } from 'src/models/Session.model';

import { captureError } from 'src/utils/errorReporting';
import { getFilenameFromUrl } from 'src/utils/getFilenameFromUrl';

import useHandleHttpRequestError from '../useHandleHttpRequestError';
import { useCameraHeartbeatWatch } from './useCameraHeartbeatWatch';
import {
  STALL_TIMEOUT_MS,
  useCaptureStallDetector,
} from './useCaptureStallDetector';
import { useGetCurrentCaptureProcess } from './useGetCurrentCaptureProcess';

const recordEyeGains = async (
  orderId: string,
  cameraData: CameraData | undefined,
  sides: EyePropertyCode[],
): Promise<void> => {
  if (!cameraData) {
    return;
  }
  const { iid } = useRemoteBoundStore.getState().session;
  const mainGain = cameraData.mainGain.data;
  const sideGain = cameraData.sideGain.data;

  await updateExamFieldsIDB(orderId, iid, {
    ...(sides.includes(EyePropertyCode.LEFT)
      ? { LeftEyeMainGain: mainGain, LeftEyeSideGain: sideGain }
      : {}),
    ...(sides.includes(EyePropertyCode.RIGHT)
      ? { RightEyeMainGain: mainGain, RightEyeSideGain: sideGain }
      : {}),
  });
};

const useCaptureImage = ({
  watchConnectionIssues = true,
}: { watchConnectionIssues?: boolean } = {}) => {
  const {
    setLeftUrl,
    leftUrl,
    setRightUrl,
    rightUrl,
    isCapturing,
    setIsCapturing,
    cameraData,
    isCanceling,
    setIsCanceling,
    hasStarted,
    setHasStarted,
    session,
    incrementLeftImageAttempts,
    incrementRightImageAttempts,
  } = useRemoteBoundStore();

  const { setEyeImage } = useIdbSync();

  const { handleHttpRequestError } = useHandleHttpRequestError();
  const { t } = useTranslation(I18NextNS.COMMON);

  const [hasTimedOutReadImage, setHasTimedOutReadImage] = useState(false);
  const requestedTargetRef = useRef<EyesPosition | null>(null);

  const emitImagingStartRequested = useCallback(
    (target: EyesPosition) => {
      requestedTargetRef.current = target;
      captureCameraEvent(EXAM_EVENTS.IMAGING_START_REQUESTED, {
        target,
        ...(cameraData?.batteryInfo?.battCap != null
          ? { camera_battery_pct: cameraData.batteryInfo.battCap }
          : {}),
        ...(cameraData?.storage?.usage
          ? { camera_storage_usage: cameraData.storage.usage }
          : {}),
      });
    },
    [cameraData],
  );

  const captureBothImages = useCallback(async () => {
    if (!session.orderId) {
      throw new Error(
        t('message.error.orderNotFound', { ns: I18NextNS.COMMON }),
      );
    }
    try {
      setLeftUrl('');
      setRightUrl('');
      setIsCapturing(true);
      incrementLeftImageAttempts();
      incrementRightImageAttempts();
      const cameraData = await getCameraDataWithRefreshedGains();
      emitImagingStartRequested(EyesPosition.BOTH);

      await recordEyeGains(session.orderId, cameraData, [
        EyePropertyCode.LEFT,
        EyePropertyCode.RIGHT,
      ]);
      await CameraConnection.getInstance().autoTakeImage();
    } catch (err) {
      captureError(err, 'Failed to capture both images');
      reportCameraConnectionIssue(CameraConnectionIssueReason.COMMAND_FAILED);
    }
  }, [
    session.orderId,
    t,
    setLeftUrl,
    setRightUrl,
    setIsCapturing,
    emitImagingStartRequested,
    incrementLeftImageAttempts,
    incrementRightImageAttempts,
  ]);

  const captureLeftImage = useCallback(async () => {
    if (!session.orderId) {
      throw new Error(
        t('message.error.orderNotFound', { ns: I18NextNS.COMMON }),
      );
    }

    try {
      setLeftUrl('');
      setIsCapturing(true);
      incrementLeftImageAttempts();
      const cameraData = await getCameraDataWithRefreshedGains();
      emitImagingStartRequested(EyesPosition.LEFT);
      await recordEyeGains(session.orderId, cameraData, [EyePropertyCode.LEFT]);
      await CameraConnection.getInstance().autoTakeImage({
        eyetype: CaptureModeEnum.LEFT_EYE,
      });
    } catch (err) {
      captureError(err, 'Failed to capture left image');
      reportCameraConnectionIssue(CameraConnectionIssueReason.COMMAND_FAILED);
    }
  }, [
    session.orderId,
    t,
    setLeftUrl,
    setIsCapturing,
    emitImagingStartRequested,
    incrementLeftImageAttempts,
  ]);

  const captureRightImage = useCallback(async () => {
    if (!session.orderId) {
      throw new Error(
        t('message.error.orderNotFound', { ns: I18NextNS.COMMON }),
      );
    }

    try {
      setRightUrl('');
      setIsCapturing(true);
      incrementRightImageAttempts();
      const cameraData = await getCameraDataWithRefreshedGains();
      emitImagingStartRequested(EyesPosition.RIGHT);
      await recordEyeGains(session.orderId, cameraData, [
        EyePropertyCode.RIGHT,
      ]);
      await CameraConnection.getInstance().autoTakeImage({
        eyetype: CaptureModeEnum.RIGHT_EYE,
      });
    } catch (err) {
      captureError(err, 'Failed to capture right image');
      reportCameraConnectionIssue(CameraConnectionIssueReason.COMMAND_FAILED);
    }
  }, [
    session.orderId,
    t,
    setRightUrl,
    setIsCapturing,
    emitImagingStartRequested,
    incrementRightImageAttempts,
  ]);

  const checkImageAvailability = useCallback(
    (
      imgUrl: string,
      onSuccess: (imgUrl: string) => void,
      onError: (errorMsg: string) => void,
    ) => {
      const maxAttempts = 30;
      let countAttempt = 1;

      const interval = setInterval(async () => {
        try {
          if (countAttempt > maxAttempts) {
            clearInterval(interval);

            if (!hasTimedOutReadImage) {
              setHasTimedOutReadImage(true);

              Sentry.captureMessage(
                `Failed to read the image file ${imgUrl} in ${maxAttempts} seconds.`,
              );

              return onError(
                t('message.error.readImageFileTimeout', {
                  imgUrl,
                  maxAttempts,
                }),
              );
            }
          }

          countAttempt++;

          await axios.head(imgUrl);
          clearInterval(interval);
          return onSuccess(imgUrl);
        } catch (err) {
          if (
            axios.isAxiosError(err) &&
            err.response &&
            err.response.status !== 404
          ) {
            clearInterval(interval);

            captureError(err, `Failed to read the image file ${imgUrl}`);

            return onError(
              t('message.error.failedToReadImageFile', {
                imgUrl,
              }),
            );
          }
        }
      }, 1000);
    },
    [hasTimedOutReadImage, t],
  );

  const handleImageProcess = useCallback(
    (filename: string): Promise<string> => {
      const imgUrl = `http://${cameraData?.ip}:8901/media/${filename}`;

      return new Promise((resolve, reject) => {
        checkImageAvailability(
          imgUrl,
          (successUrl: string) => {
            resolve(successUrl);
          },
          (errorMsg: string) => {
            reportCameraConnectionIssue(
              CameraConnectionIssueReason.CANT_GET_IMAGE,
            );
            reject(new Error(errorMsg));
          },
        );
      });
    },
    [cameraData?.ip, checkImageAvailability],
  );

  const { currentCaptureProcess } = useGetCurrentCaptureProcess();

  useCameraHeartbeatWatch({ enabled: watchConnectionIssues });

  useCaptureStallDetector({
    enabled: watchConnectionIssues,
    isCapturing,
    captureState: currentCaptureProcess?.state,
    isPupilSmall: currentCaptureProcess?.isPupilSmall ?? false,
    timeoutMs: STALL_TIMEOUT_MS,
    onTimeout: () =>
      reportCameraConnectionIssue(CameraConnectionIssueReason.CANT_GET_IMAGE),
  });

  const inFlightEyes = useRef(new Set<EyePropertyCode>());

  const fetchAndStoreEyeImage = useCallback(
    async (
      filename: string,
      imagingTarget: EyesPosition,
      eyePropertyCode: EyePropertyCode,
      setUrl: (url: string) => void,
    ) => {
      if (inFlightEyes.current.has(eyePropertyCode)) {
        return;
      }
      inFlightEyes.current.add(eyePropertyCode);

      try {
        const { result: imgUrl, timingProps } = await withNetworkTiming(
          () => handleImageProcess(filename),
          { transport: NETWORK_TRANSPORT.LAN },
        );

        captureCameraEvent(EXAM_EVENTS.IMAGE_RETRIEVED, {
          ...timingProps,
          target: imagingTarget,
        });

        const imageFileName = getFilenameFromUrl(imgUrl);
        const blob = await fetch(imgUrl, { cache: 'no-store' }).then((r) =>
          r.blob(),
        );

        if (session.orderId) {
          await setEyeImage(
            session.orderId,
            session.iid,
            eyePropertyCode,
            blob,
            imageFileName,
          );
        }
        setUrl(URL.createObjectURL(blob));
      } finally {
        inFlightEyes.current.delete(eyePropertyCode);
      }
    },
    [handleImageProcess, session.orderId, session.iid, setEyeImage],
  );

  const getImageUrl = useCallback(async () => {
    const { state, filename } = currentCaptureProcess ?? { filename: [] };

    switch (state) {
      case StatusOfShootingEnum.COMPLETE_LEFT: {
        if (!isCapturing) {
          return;
        }
        try {
          if (leftUrl) {
            return;
          }
          await fetchAndStoreEyeImage(
            filename[0],
            EyesPosition.LEFT,
            EyePropertyCode.LEFT,
            setLeftUrl,
          );
        } catch (error) {
          handleHttpRequestError(error);
        }
        break;
      }

      case StatusOfShootingEnum.COMPLETE_RIGHT: {
        if (!isCapturing) {
          return;
        }
        try {
          if (rightUrl) {
            return;
          }
          await fetchAndStoreEyeImage(
            filename[0],
            EyesPosition.RIGHT,
            EyePropertyCode.RIGHT,
            setRightUrl,
          );
        } catch (error) {
          handleHttpRequestError(error);
        }
        break;
      }

      case StatusOfShootingEnum.RUNNING: {
        if (!hasStarted) {
          captureCameraEvent(EXAM_EVENTS.IMAGING_STARTED, {
            target: requestedTargetRef.current ?? EyesPosition.BOTH,
          });
          setHasStarted(true);
        }
        setHasTimedOutReadImage(false);
        break;
      }

      case StatusOfShootingEnum.COMPLETE: {
        // When the user click capture button after some image has been captured, the status is complete and then go to running status.
        if (hasStarted) {
          setIsCapturing(false);
          setHasStarted(false);
        }

        // When user click cancel button after some image has been captured.
        if (isCanceling) {
          setIsCanceling(false);
        }

        break;
      }

      case StatusOfShootingEnum.IDLE: {
        // When user click cancel button before any image has been captured.
        if (isCanceling) {
          setIsCapturing(false);
          setIsCanceling(false);
          setHasStarted(false);
        }

        break;
      }

      default: {
        break;
      }
    }
  }, [
    currentCaptureProcess,
    fetchAndStoreEyeImage,
    leftUrl,
    handleHttpRequestError,
    rightUrl,
    isCapturing,
    setHasStarted,
    hasStarted,
    isCanceling,
    setIsCapturing,
    setIsCanceling,
    setLeftUrl,
    setRightUrl,
  ]);

  useEffect(() => {
    getImageUrl();
  }, [getImageUrl]);

  return { captureBothImages, captureLeftImage, captureRightImage };
};

export default useCaptureImage;
